import axios from 'axios'
import queryString from 'query-string'
import { camelizeKeys } from 'humps'

import type {
  AuthenticateResponse,
  AuthenticateRequestBody,
  RegisterRequestBody,
  RegisterResponse,
  FetchUserResponse,
  User,
} from './types'
import cookies from 'services/cookies'
import errorLogging from 'services/error-logging'

const URL = process.env.SSO_URL
const CLIENT_ID = process.env.SSO_CLIENT_ID
const CLIENT_SECRET = process.env.SSO_CLIENT_SECRET

const CLIENT_CREDENTIALS_GRANT_TYPE = 'client_credentials'
const PASSWORD_GRANT_TYPE = 'password'

function padLeft(number: number) {
  return number < 10 ? `0${number}` : number
}

function parseBirthday(
  birthYear: string,
  birthMonth: string,
  birthDay: string,
) {
  return `${birthYear}-${padLeft(Number(birthMonth) + 1)}-${padLeft(
    Number(birthDay),
  )}`
}

function applicationToken() {
  return axios
    .post(
      `${URL}/login`,
      {
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        grant_type: CLIENT_CREDENTIALS_GRANT_TYPE,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
    .then((result) => result.data.access_token)
}

export async function logout() {
  const token = await applicationToken()
  return axios.post(
    `${URL}/logout`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  )
}

export async function authenticate({
  email,
  password,
}: AuthenticateRequestBody) {
  const { data } = await axios.post(
    `${URL}/login`,
    {
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      grant_type: PASSWORD_GRANT_TYPE,
      username: email,
      password,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )
  return (camelizeKeys(data) as unknown) as AuthenticateResponse
}

export async function register({
  email,
  password,
  firstName,
  lastName,
  birthYear,
  birthMonth,
  birthDay,
}: RegisterRequestBody) {
  const token = await applicationToken()

  return axios
    .post(
      `${URL}/api/v1/users`,
      queryString.stringify({
        email,
        password,
        first: firstName,
        last: lastName,
        birthday: parseBirthday(birthYear, birthMonth, birthDay),
      }),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${token}`,
        },
      },
    )
    .then(({ data }) => {
      return (camelizeKeys(data) as unknown) as RegisterResponse
    })
    .catch((e) => {
      if (!e.response || e.response.status !== 401) {
        errorLogging(new Error(`failed registering`), {
          error: e,
        })
      }

      if (e.response && e.response.data && e.response.data.error_description) {
        throw new Error(e.response.data.error_description)
      }
      throw new Error(e)
    })
}

async function getBearerToken() {
  const cookieToken = cookies.getAccessToken()

  if (!cookieToken) {
    const appToken = await applicationToken()
    cookies.saveAccessToken(appToken)
    return appToken
  } else {
    return Promise.resolve(cookieToken)
  }
}

export async function fetchUser() {
  const token = await getBearerToken()

  const { data } = await axios.get<FetchUserResponse>(
    `${URL}/api/v1/oauth2/me`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (data.name === 'react') {
    return
  } else {
    const { email, emailVerified, firstName, lastName, userId } = (camelizeKeys(
      data,
    ) as unknown) as User
    return {
      email,
      emailVerified,
      firstName,
      lastName,
      userId,
    }
  }
}
